import { useState, useMemo } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import AuthShell from "./AuthShell";

const MIN_LEN = 8;

export default function ResetPassword() {
  const navigate = useNavigate();
  const location = useLocation();
  const token = useMemo(() => new URLSearchParams(location.search).get("token") || "", [location.search]);
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const pwLongEnough = password.length >= MIN_LEN;
  const matches = password && password === confirm;
  const canSubmit = pwLongEnough && matches;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!token) { setErr("This link is missing a reset token. Please request a new one."); return; }
    if (!canSubmit) { setErr("Enter a password of at least 8 characters and make sure both fields match."); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success("Password updated. Sign in with your new password.");
      navigate("/login");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Reset failed. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthShell
        title="Reset link required"
        subtitle="This page needs a token from the password reset email you received."
        testId="reset-missing-token-page"
        footer={<Link to="/forgot-password" className="text-purple-700 font-semibold hover:underline" data-testid="reset-request-link">Request a new reset link</Link>}
      >
        <div className="text-sm bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-amber-900" data-testid="reset-missing-notice">
          The reset link opens this page automatically. If you&apos;re seeing this, the link may have been broken or copied incorrectly.
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Choose a new password"
      subtitle="Pick something you&apos;ll remember but that no one else could guess."
      testId="reset-page"
      footer={<Link to="/login" className="text-purple-700 font-semibold hover:underline" data-testid="reset-back-link">Back to sign-in</Link>}
    >
      <form onSubmit={submit} className="space-y-4" data-testid="reset-form">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">New password</label>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="reset-password"
              className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 pr-11 outline-none transition"
              placeholder="At least 8 characters"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              data-testid="reset-toggle-pw"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Confirm new password</label>
          <input
            type={showPw ? "text" : "password"}
            autoComplete="new-password"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            data-testid="reset-confirm"
            className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 outline-none transition"
            placeholder="Re-enter password"
          />
          <div className="mt-2 text-xs flex items-center gap-1.5" data-testid="reset-match-hint">
            {matches
              ? <><Check className="w-3.5 h-3.5 text-emerald-600" /><span className="text-emerald-700">Passwords match</span></>
              : <><X className="w-3.5 h-3.5 text-slate-400" /><span className="text-slate-500">Passwords must match</span></>}
          </div>
        </div>
        {err && <div data-testid="reset-error" className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2">{err}</div>}
        <button
          type="submit"
          disabled={loading || !canSubmit}
          data-testid="reset-submit"
          className="w-full inline-flex items-center justify-center gap-2 iti-btn-primary px-4 py-3 font-semibold disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Update password"}
        </button>
      </form>
    </AuthShell>
  );
}
